import { authGuard } from '@abp/ng.core';
import { Routes } from '@angular/router';
import { permissionGuard } from './core/auth/permission.guard';

export const APP_ROUTES: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'dashboard',
  },
  {
    path: 'landing',
    loadComponent: () => import('./landing/landing.component').then(c => c.LandingComponent),
  },
  {
    path: 'login',
    redirectTo: 'account/login',
    pathMatch: 'full',
  },
  {
    path: 'account/login',
    loadComponent: () => import('./auth/login.component').then(c => c.LoginComponent),
  },
  {
    path: 'dashboard',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Dashboard' },
    loadComponent: () => import('./dashboard/dashboard.component').then(c => c.DashboardComponent),
  },
  { path: 'home', redirectTo: 'dashboard', pathMatch: 'full' },
  {
    path: 'account',
    loadChildren: () => import('@abp/ng.account').then(c => c.createRoutes()),
  },
  {
    path: 'identity',
    loadChildren: () => import('@abp/ng.identity').then(c => c.createRoutes()),
  },
  {
    path: 'tenant-management',
    loadChildren: () => import('@abp/ng.tenant-management').then(c => c.createRoutes()),
  },
  {
    path: 'setting-management',
    loadChildren: () => import('@abp/ng.setting-management').then(c => c.createRoutes()),
  },
  {
    path: 'financial-settings',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Admin.Global' },
    loadComponent: () => import('./financial-settings/financial-settings.component').then(c => c.FinancialSettingsComponent),
  },
  {
    path: 'malls',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Malls' },
    loadComponent: () => import('./malls/mall-list.component').then(c => c.MallListComponent),
  },
  {
    path: 'shops',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Shops' },
    loadComponent: () => import('./shops/shop-list.component').then(c => c.ShopListComponent),
  },
  {
    path: 'tenants',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Tenants' },
    loadComponent: () => import('./tenants/tenant-list.component').then(c => c.TenantListComponent),
  },
  {
    path: 'contracts',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Rentals' },
    loadComponent: () =>
      import('./contracts/contract-list.component').then(c => c.ContractListComponent),
  },
  {
    path: 'contracts/:contractId/statement',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.ContractAccounts' },
    loadComponent: () =>
      import('./contract-account/contract-account-statement.component').then(c => c.ContractAccountStatementComponent),
  },
  {
    path: 'contract-account/:contractId',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.ContractAccounts' },
    loadComponent: () =>
      import('./contract-account/contract-account-statement.component').then(c => c.ContractAccountStatementComponent),
  },
  {
    path: 'billing-documents',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.FinancialDocuments.View' },
    loadComponent: () =>
      import('./billing-documents/billing-documents.component').then(c => c.BillingDocumentsComponent),
  },
  {
    path: 'electricity',
    redirectTo: 'electricity/meters',
    pathMatch: 'full',
  },
  {
    path: 'electricity/meters',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Utilities' },
    loadComponent: () =>
      import('./electricity/meter-list.component').then(c => c.MeterListComponent),
  },
  {
    path: 'electricity/bills',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Utilities' },
    loadComponent: () =>
      import('./electricity/bill-list.component').then(c => c.BillListComponent),
  },
  {
    path: 'electricity/batch-ocr',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Utilities.BatchOcr' },
    loadComponent: () =>
      import('./electricity/batch-ocr.component').then(c => c.BatchOcrComponent),
  },
  {
    path: 'electricity/billing-units',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Utilities' },
    loadComponent: () =>
      import('./electricity/billing-unit-list.component').then(c => c.BillingUnitListComponent),
  },
  {
    path: 'electricity/supply-categories',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.UtilitySupplyCategories' },
    loadComponent: () =>
      import('./electricity/utility-supply-category-list.component').then(c => c.UtilitySupplyCategoryListComponent),
  },
  {
    path: 'electricity/billing-report',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Reports.View' },
    loadComponent: () =>
      import('./electricity/utility-billing-report.component').then(c => c.UtilityBillingReportComponent),
  },
  {
    path: 'rent',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.RentPayments' },
    loadComponent: () =>
      import('./rent/rent-collection.component').then(c => c.RentCollectionComponent),
  },
  {
    path: 'discount-policies',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.DiscountPolicies' },
    loadComponent: () =>
      import('./discount-policies/discount-policy-list.component').then(c => c.DiscountPolicyListComponent),
  },
  {
    path: 'accounting-periods',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Accounting' },
    loadComponent: () =>
      import('./accounting/period-management.component').then(c => c.PeriodManagementComponent),
  },
  {
    path: 'accounting/periods',
    redirectTo: 'accounting-periods',
    pathMatch: 'full',
  },
  {
    path: 'cash-remittances',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Remittances' },
    loadComponent: () =>
      import('./accounting/cash-remittances.component').then(c => c.CashRemittancesComponent),
  },
  {
    path: 'accounting/cash-remittances',
    redirectTo: 'cash-remittances',
    pathMatch: 'full',
  },
  {
    path: 'ancillary-services',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.ServiceSubscriptions' },
    loadComponent: () =>
      import('./ancillary-services/service-subscription-list.component').then(c => c.ServiceSubscriptionListComponent),
  },
  {
    path: 'fines',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Fines' },
    loadComponent: () =>
      import('./fines/fine-list.component').then(c => c.FineListComponent),
  },
  {
    path: 'fine-types',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.FineTypes' },
    loadComponent: () =>
      import('./fine-types/fine-type-list.component').then(c => c.FineTypeListComponent),
  },


  // Legacy route redirects
  { path: 'shop-grid', redirectTo: 'shops', pathMatch: 'full' },
  { path: 'kanban', redirectTo: 'contracts', pathMatch: 'full' },
  { path: 'grid', redirectTo: 'electricity', pathMatch: 'full' },
  { path: 'record-form', redirectTo: 'contracts', pathMatch: 'full' },
  { path: 'electricity-meters', redirectTo: 'electricity', pathMatch: 'full' },
  { path: 'electricity-readings', redirectTo: 'electricity', pathMatch: 'full' },
  { path: 'electricity/reading', redirectTo: 'electricity', pathMatch: 'full' },
  { path: 'electricity/readings', redirectTo: 'electricity', pathMatch: 'full' },
  { path: 'electricity-bills', redirectTo: 'electricity/bills', pathMatch: 'full' },

  // Phase 11.1 — Fuel & Generator Management
  {
    path: 'fuel',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Fuel' },
    loadComponent: () => import('./fuel/fuel.component').then(c => c.FuelComponent),
  },

  // Phase 14 — Energy Analytics & Management
  {
    path: 'energy-analytics',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.EnergyAnalytics' },
    loadComponent: () =>
      import('./energy-analytics/energy-analytics.component').then(c => c.EnergyAnalyticsComponent),
  },

  {
    path: 'energy-flow',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.EnergyAnalytics.ViewEnergyFlow' },
    loadComponent: () =>
      import('./energy-flow-dashboard/energy-flow-dashboard.component').then(c => c.EnergyFlowDashboardComponent),
  },

  // Phase 15 — Data Quality, Telemetry Health & Audit Center
  {
    path: 'data-quality',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.DataQuality' },
    loadComponent: () =>
      import('./data-quality/data-quality.component').then(c => c.DataQualityComponent),
  },

  // Dedicated Assignment Management (Malls & Meters)
  {
    path: 'assignments',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Utilities' },
    loadComponent: () =>
      import('./assignments/assignment-management.component').then(c => c.AssignmentManagementComponent),
  },

  // Tenant Meter-by-Meter Financial Report
  {
    path: 'tenant-meter-financial-report',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.TenantMeterFinancialReport.View' },
    loadComponent: () =>
      import('./tenant-meter-financial-report/tenant-meter-financial-report.component').then(c => c.TenantMeterFinancialReportComponent),
  },

  // Consolidated Rent Statement (All Tenants)
  {
    path: 'reports/consolidated-rent-statement',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPolicy: 'MallManagement.Reports.View' },
    loadComponent: () =>
      import('./reports/consolidated-rent-statement.component').then(c => c.ConsolidatedRentStatementComponent),
  },
  {
    path: 'consolidated-rent-statement',
    redirectTo: 'reports/consolidated-rent-statement',
    pathMatch: 'full',
  },
];

